import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Button, Input } from '../../../components';
import { Card, CardBody, CardFooter, Form, FormGroup, Label } from 'reactstrap';
import React, { useEffect, useState } from 'react';

import { ProgramComment } from '../../../common/Types';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';

const Comments = (props: AnnualReportProps) => {
    const [comment, setComment] = useState<ProgramComment>();
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );
    const {
        control,
        handleSubmit,
        reset,
        formState: { isSubmitting, errors },
    } = useForm();

    const onSubmit = (formData): Promise<void> => {
        return new Promise((res) => {
            const toastId = toast.info('Saving comments...');

            programService
                .saveProgramComment(props.program.programId, {
                    ...new ProgramComment({
                        ...formData,
                        commentId: formData.commentId ?? comment?.commentId ?? 0,
                        programId: formData.programId ?? props.program.programId,
                        activeYear: formData.activeYear ?? +props.reportYear,
                    }),
                })
                .then((result) => {
                    // Update form with server response (especially commentId for new records)
                    if (result) {
                        setComment(result);
                        reset(result);
                    }
                    toast.update(toastId, {
                        type: 'success',
                        render: 'Comments Saved.',
                    });

                    if (submitDirection === AnnualReportNavigationDirection.Forward) {
                        props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                    }
                })
                .catch(() => {
                    toast.update(toastId, {
                        type: 'error',
                        render: 'Failed to save comments.',
                    });
                })
                .finally(() => {
                    res();
                });
        });
    };

    useEffect(() => {
        const getComment = () => {
            programService
                .getProgramComment(props.program.programId, props.reportYear ?? new Date().getFullYear())
                .then((response) => {
                    setComment(response);
                    if (response) {
                        reset(response);
                    }
                })
                .catch(() => {
                    toast.error('Unable to get comments.');
                });
        };

        getComment();
    }, [props.program.programId, props.reportYear, setComment]);

    return (
        <Card>
            <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                const fieldNames = Object.keys(validationErrors);
                toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
            })}>
                <CardBody>
                    <h4 className="card-title">Comments</h4>
                    <h5 className="card-subtitle mb-3">
                        {`Please provide your comments, suggestions or concerns relating specifically to your Program or
                        generally to ${props.profession.title} education.
                        
                        If you do not have any comments, please enter 'No comments'`}
                    </h5>
                    <FormGroup>
                        <Controller
                            name="comment"
                            control={control}
                            rules={{
                                required: `Comments are required, if you have no comment, enter 'No comments'`,
                            }}
                            render={({ field }) => (
                                <Input
                                    type="textarea"
                                    {...field}
                                    value={field.value ?? comment?.comment ?? ''}
                                    rows={20}
                                />
                            )}
                        />
                        {errors?.comment?.message && (
                            <div>
                                <Label className={'text-danger'}>{errors?.comment?.message as string}</Label>
                            </div>
                        )}
                        <Controller
                            name="commentId"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? comment?.commentId ?? 0} />
                            )}
                        />
                        <Controller
                            name="programId"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? props.program.programId} />
                            )}
                        />
                        <Controller
                            name="activeYear"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? +props.reportYear} />
                            )}
                        />
                        <Controller
                            name="createdBy"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? comment?.createdBy ?? ''} />
                            )}
                        />
                        <Controller
                            name="dateCreated"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="hidden"
                                    {...field}
                                    value={field.value ?? comment?.dateCreated?.toString() ?? ''}
                                />
                            )}
                        />
                        <Controller
                            name="lastEdited"
                            control={control}
                            render={({ field }) => (
                                <input
                                    type="hidden"
                                    {...field}
                                    value={field.value ?? comment?.lastEdited?.toString() ?? ''}
                                />
                            )}
                        />
                        <Controller
                            name="editedBy"
                            control={control}
                            render={({ field }) => (
                                <input type="hidden" {...field} value={field.value ?? comment?.editedBy ?? ''} />
                            )}
                        />
                    </FormGroup>
                </CardBody>
                {props.canEdit && (
                    <CardFooter className={'d-flex justify-content-between'}>
                        <Button
                            type={'button'}
                            color={'warning'}
                            onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                        >
                            <i className={'mdi mdi-arrow-left'} />
                            <span className={'mr-1'}>{`Previous`}</span>
                        </Button>

                        <Button
                            disabled={isSubmitting}
                            type={'submit'}
                            color={'primary'}
                            onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Stay)}
                        >
                            <i className={'mdi mdi-content-save'} />
                            <span className={'ml-1'}>{`Save`}</span>
                        </Button>

                        <Button
                            disabled={isSubmitting}
                            type={'submit'}
                            color={'info'}
                            onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                        >
                            <span className={'mr-1'}>{`Save & Continue`}</span>
                            <i className={'mdi mdi-arrow-right'} />
                        </Button>
                    </CardFooter>
                )}
            </Form>
        </Card>
    );
};

export default Comments;
